import type { Language } from '@/i18n/config'

/* A typed date, both ways: what the DatePicker's text half shows for a stored
 * value, and what it makes of what somebody typed.
 *
 * The wire format never changes. The control's value, the form draft and the
 * request body are ISO `YYYY-MM-DD` (AD-19) in both languages; only the text in
 * the box is localised, and only for Russian — a Russian reader writes
 * 31.10.2026, and the Yiwu office's own order is ISO, which the English UI
 * keeps showing.
 *
 * ONE parser for both languages, so a date copied out of a Russian document
 * into the English UI is still a date. It reads every non-ISO form DAY FIRST
 * and never month-first: nobody this product serves writes 10/31/2026, so
 * `03/04/2026` is the 3rd of April and `10/31/2026` is refused. A misparse
 * cannot hide either way — the control echoes the canonical form on commit.
 *
 * Pure and session-free, so the table of accepted and refused forms is a unit
 * test (`dateInput.test.ts`) rather than a story told through a rendered
 * picker. */

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/u

/** Day, month, four-digit year, separated by `.`, `/` or `,` (each with
 *  optional spaces around it) or by spaces alone. Not by `-`: a hyphen is the
 *  ISO separator, and `31-10-2026` is neither form. */
const DAY_FIRST = /^(\d{1,2})(?:\s*[./,]\s*|\s+)(\d{1,2})(?:\s*[./,]\s*|\s+)(\d{4})$/u

/** `DDMMYYYY`. The iOS `numeric` keypad the control asks for has neither "."
 *  nor "-", so eight bare digits is the only date a phone user can type there. */
const EIGHT_DIGITS = /^(\d{2})(\d{2})(\d{4})$/u

/** The years an eight-digit run may name. Bounded because the run carries no
 *  separator to say where the year is: `20261031`, typed year-first, must be
 *  refused rather than read as day 20 of month 26 of 1031 — which the calendar
 *  check alone would catch only by luck. */
const EIGHT_DIGIT_YEARS = { min: 1900, max: 2100 } as const

/** ISO for a real calendar day, or null — the 31st of a 30-day month and the
 *  29th of February outside a leap year are refused, not rolled forward. */
function isoOf(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1) return null
  const date = new Date(Date.UTC(year, month - 1, day))
  // Date.UTC maps 0–99 onto 1900–1999; a four-digit year never takes that path.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** What somebody typed, as ISO `YYYY-MM-DD`, or null when it is not a date
 *  this control accepts. */
export function parseDateInput(text: string): string | null {
  const value = text.trim()

  const iso = ISO.exec(value)
  if (iso) return isoOf(Number(iso[1]), Number(iso[2]), Number(iso[3]))

  const dayFirst = DAY_FIRST.exec(value)
  if (dayFirst) {
    return isoOf(Number(dayFirst[3]), Number(dayFirst[2]), Number(dayFirst[1]))
  }

  const digits = EIGHT_DIGITS.exec(value)
  if (digits) {
    const year = Number(digits[3])
    if (year < EIGHT_DIGIT_YEARS.min || year > EIGHT_DIGIT_YEARS.max) return null
    return isoOf(year, Number(digits[2]), Number(digits[1]))
  }

  return null
}

/** The text the control shows for a stored ISO value: `31.10.2026` in
 *  Russian, the ISO value itself in English. A value this module cannot read is
 *  shown exactly as stored — a picker never drops a value it does not
 *  recognise, and an empty box would say "no date" about a record that has
 *  one. */
export function displayDateInput(value: string, language: Language): string {
  if (language !== 'ru') return value
  const iso = ISO.exec(value)
  if (!iso || isoOf(Number(iso[1]), Number(iso[2]), Number(iso[3])) === null) return value
  return `${iso[3]}.${iso[2]}.${iso[1]}`
}
